import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Calculator, Zap, FileCheck, Award, AlertTriangle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const PsiValueCalculations = () => {
  const faqs = [
    {
      question: "What is a Psi (ψ) Value?",
      answer: "A Psi value (ψ-value, linear thermal transmittance) measures heat loss at junctions where building elements meet—such as wall-floor junctions, window reveals, corners and eaves. It's measured in W/mK and represents the additional heat loss per metre length of the junction beyond what the U-values of the elements alone would predict."
    },
    {
      question: "Why are Psi Values important?",
      answer: "Thermal bridges at junctions can account for 20-30% of total heat loss in well-insulated buildings. Accurate Psi values are essential for SAP calculations, meeting Part L requirements, and avoiding condensation and mould risks. Using calculated Psi values instead of defaults can significantly improve your SAP rating."
    },
    {
      question: "When should I use calculated vs default Psi values?",
      answer: "Building Regulations allow use of default (Accredited Construction Details) Psi values, but these are often conservative. Calculated Psi values based on your actual details typically show better performance, improving SAP ratings. For challenging projects or marginal compliance, calculated values are essential."
    },
    {
      question: "What junctions need Psi value calculations?",
      answer: "Common junctions include: wall-floor (ground and intermediate), wall-roof (eaves, verge, ridge), wall-wall (corners), window/door perimeters (head, jamb, sill), party wall junctions, balconies and other penetrations. We calculate all relevant junctions for your project."
    },
    {
      question: "How are Psi values calculated?",
      answer: "We use 2D thermal modelling software compliant with BR 497 and BS EN ISO 10211. The junction geometry and materials are modelled, and heat flow is simulated to determine the linear thermal transmittance. Results are validated against industry standards and presented in a format suitable for SAP."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Psi Value Calculations | Thermal Bridging | Linear Thermal Transmittance | Energy Performance Direct</title>
        <meta name="description" content="Professional Psi value calculations for thermal bridging analysis. Junction heat loss calculations for SAP and Part L compliance. Improve your energy rating. Call 01392 980077." />
        <meta name="keywords" content="psi value calculations, thermal bridging, linear thermal transmittance, junction heat loss, SAP thermal bridges, Part L compliance, building junction analysis" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/psi-value-calculations" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-orange-50 via-yellow-50 to-orange-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-orange-600">Thermal Bridging Analysis</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Psi Value Calculations
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Professional linear thermal transmittance calculations for building junctions. Reduce thermal bridging, improve SAP ratings and demonstrate Part L compliance.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-orange-600" />
                  <span className="text-sm font-medium">From £75/junction</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-orange-600" />
                  <span className="text-sm font-medium">BR 497 Compliant</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-orange-600" />
                  <span className="text-sm font-medium">SAP Ready Output</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-orange-600 hover:bg-orange-700">
                    Request Psi Value Quote
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button size="lg" variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                    <Phone className="mr-2 h-5 w-5" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Junction Types We Calculate</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Zap className="h-12 w-12 text-orange-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Wall-Floor Junctions</h3>
                    <p className="text-gray-600">
                      Ground floor perimeter junctions (E1-E5), intermediate floor junctions (E6-E7). Critical for SAP y-values and heat loss calculations.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Zap className="h-12 w-12 text-orange-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Wall-Roof Junctions</h3>
                    <p className="text-gray-600">
                      Eaves (E16-E17), verge, gable, ridge and flat roof junctions. Room-in-roof details including dormer windows.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Zap className="h-12 w-12 text-orange-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Window & Door Reveals</h3>
                    <p className="text-gray-600">
                      Head, jamb and sill details (E10-E15). Window installation positions optimised to minimise thermal bridging.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Zap className="h-12 w-12 text-orange-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Corner Junctions</h3>
                    <p className="text-gray-600">
                      External and internal wall corners (E8-E9). Corner details can significantly impact overall thermal performance.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Zap className="h-12 w-12 text-orange-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Party Wall Junctions</h3>
                    <p className="text-gray-600">
                      Party wall to external wall, floor and roof junctions (E19-E24). Essential for terraced and semi-detached dwellings.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Zap className="h-12 w-12 text-orange-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Special Details</h3>
                    <p className="text-gray-600">
                      Balconies, canopies, structural penetrations, steel frames and other non-standard junctions requiring bespoke analysis.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Why Calculate Psi Values?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-white">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-8 w-8 text-red-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Using Default Values</h3>
                        <ul className="text-gray-600 space-y-2">
                          <li>• Conservative assumptions penalise SAP rating</li>
                          <li>• May require compensating measures elsewhere</li>
                          <li>• Doesn't reflect your actual construction quality</li>
                          <li>• Risk of marginal Part L compliance</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white border-green-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle className="h-8 w-8 text-green-600 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Using Calculated Values</h3>
                        <ul className="text-gray-600 space-y-2">
                          <li>• Accurate representation of your details</li>
                          <li>• Typically improves SAP rating by 2-5 points</li>
                          <li>• Can unlock Part L compliance on tight projects</li>
                          <li>• Demonstrates design quality to Building Control</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Psi Value Calculation FAQs</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-orange-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Need Psi Value Calculations?</h2>
            <p className="text-xl mb-8 opacity-90">
              Professional thermal bridging analysis to improve your SAP rating
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking">
                <Button size="lg" variant="secondary" className="bg-white text-orange-600 hover:bg-gray-100">
                  Request Quote
                </Button>
              </Link>
              <a href="tel:01392980077">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-orange-700">
                  <Phone className="mr-2 h-5 w-5" />
                  01392 980077
                </Button>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PsiValueCalculations;
